import { type ProjectEmployeeDayCard } from "@/lib/backend/api";

/** Why a card is in "Voor jou". Both can be true; the row says which. */
export type ForYouReason = "function" | "city" | "both";

export type ForYouMatch = { card: ProjectEmployeeDayCard; reason: ForYouReason };

export type ForYouInput = {
  /** Names of the functions this employee may work, from `userFunction.getMyFunctions()`. */
  myFunctions: string[];
  /** The employee's own city, from `me.employee.city`. */
  myCity: string | null | undefined;
};

/** How many matches the section shows before it stops being a shortcut and becomes the list again. */
export const FOR_YOU_LIMIT = 5;

/**
 * The opportunities that fit this employee, by two rules and no more.
 *
 * Deliberately not a recommendation engine. Two hard rules — you can do this work, or it is in your
 * town — because the section has to be explainable in the sentence printed on the row itself. A
 * ranking you cannot explain is one you cannot defend when someone asks why they never saw
 * something.
 *
 * Matching is on function _names_: the cards carry `availableFunctions` as strings, so that is the
 * only key both sides share. Case- and whitespace-insensitive, since these are typed by planners in
 * one place and by admins in another.
 */
export function forYou(
  cards: ProjectEmployeeDayCard[],
  { myFunctions, myCity }: ForYouInput,
  limit = FOR_YOU_LIMIT,
): ForYouMatch[] {
  const wanted = new Set(myFunctions.map(normalise).filter(Boolean));
  const city = normalise(myCity ?? "");

  const matches: ForYouMatch[] = [];

  for (const card of cards) {
    // Something you already applied for is not a suggestion.
    if (card.employeeHasApplied) {
      continue;
    }

    const byFunction =
      wanted.size > 0 &&
      (card.availableFunctions ?? []).some((name) => wanted.has(normalise(name)));
    const byCity = !!city && normalise(card.city ?? "") === city;

    if (!byFunction && !byCity) {
      continue;
    }

    matches.push({
      card,
      reason: byFunction && byCity ? "both" : byFunction ? "function" : "city",
    });
  }

  // Soonest first, and among things on the same day the ones that fit twice.
  //
  // It was the other way round — a double match anywhere beat a single match tomorrow — and that was
  // defensible while Ontdek fetched one month. It stopped being defensible when the list became a
  // three-month timeline: the section led with something six weeks out while a shift that fitted you
  // just as well sat two rows below it, unmentioned. A shortcut to next quarter is not a shortcut.
  const rank = { both: 0, function: 1, city: 2 } as const;

  const sorted = matches.sort(
    (a, b) => a.card.day.localeCompare(b.card.day) || rank[a.reason] - rank[b.reason],
  );

  // One row per project, the soonest day it runs.
  //
  // A card is a project-*day*, so a project that runs a fortnight offers a fortnight of them, all
  // matching for the same reason and all looking identical on a row. Five slots filled by one
  // project four times over is not five suggestions — and against a three-month window that is the
  // ordinary case, not the edge one. Which day it also runs is a question for the project screen.
  const seen = new Set<string>();

  return sorted
    .filter(({ card }) => {
      if (seen.has(card.projectId)) {
        return false;
      }
      seen.add(card.projectId);
      return true;
    })
    .slice(0, limit);
}

function normalise(value: string): string {
  return value.trim().toLowerCase();
}
